import {
  DEFAULT_PROFILE_ID,
  findProfile,
  OPINIONS_BY_ID,
  type Overrides,
} from "@kedaat/definitions";
import { formatPlainDate, parsePlainDate, type PlainDate } from "@kedaat/engine";
import { CANDLE_MINUTE_CHOICES, findLocality } from "@kedaat/locations";

export interface Settings {
  locality: number;
  profile: string;
  overrides: Overrides;
  seconds: boolean;
  /** Candle-lighting minutes chosen by the user, by locality code. */
  candleMinutes: Record<number, number>;
}

export const DEFAULT_SETTINGS: Settings = {
  locality: 3000, // ירושלים
  profile: DEFAULT_PROFILE_ID,
  overrides: {},
  seconds: false,
  candleMinutes: {},
};

const STORAGE_KEY = "kedaat.settings.v1";

/** Drops anything invalid, so stale storage or a hand-edited link can never break the page. */
export function sanitizeSettings(input: Partial<Settings>): Settings {
  const locality =
    typeof input.locality === "number" && findLocality(input.locality)
      ? input.locality
      : DEFAULT_SETTINGS.locality;
  const profile = findProfile(String(input.profile))
    ? String(input.profile)
    : DEFAULT_SETTINGS.profile;
  const profileOpinions = findProfile(profile)!.opinions;

  const overrides: Overrides = {};
  for (const id of Object.values(input.overrides ?? {})) {
    const opinion = typeof id === "string" ? OPINIONS_BY_ID.get(id) : undefined;
    if (opinion && profileOpinions[opinion.zman] !== opinion.id)
      overrides[opinion.zman] = opinion.id;
  }

  const candleMinutes: Record<number, number> = {};
  for (const [code, minutes] of Object.entries(input.candleMinutes ?? {})) {
    if (
      (CANDLE_MINUTE_CHOICES as readonly number[]).includes(minutes) &&
      findLocality(Number(code))
    ) {
      candleMinutes[Number(code)] = minutes;
    }
  }

  return { locality, profile, overrides, seconds: input.seconds === true, candleMinutes };
}

export function loadStoredSettings(): Partial<Settings> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<Settings>) : {};
  } catch {
    return {};
  }
}

export function storeSettings(settings: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage may be unavailable (private mode); settings still live in the URL.
  }
}

/** URL parameters take precedence over stored settings, so a shared link shows the same view. */
export function settingsFromUrl(
  params: URLSearchParams,
  stored: Partial<Settings>,
): { settings: Settings; date: PlainDate | null } {
  const merged: Partial<Settings> = { ...stored };
  if (params.has("city")) merged.locality = Number(params.get("city"));
  if (params.has("profile")) {
    merged.profile = params.get("profile")!;
    merged.overrides = {};
  }
  if (params.has("o")) {
    merged.overrides = Object.fromEntries(
      params
        .get("o")!
        .split(",")
        .filter(Boolean)
        .map((id) => [id.split(".")[0], id]),
    );
  }
  if (params.has("sec")) merged.seconds = params.get("sec") === "1";
  if (params.has("candle") && merged.locality !== undefined) {
    merged.candleMinutes = {
      ...merged.candleMinutes,
      [merged.locality]: Number(params.get("candle")),
    };
  }

  let date: PlainDate | null = null;
  try {
    const iso = params.get("date");
    if (iso) date = parsePlainDate(iso);
  } catch {
    date = null;
  }
  return { settings: sanitizeSettings(merged), date };
}

export function settingsToQuery(
  settings: Settings,
  date: PlainDate | null,
  options: { omitCity?: boolean } = {},
): string {
  const params = new URLSearchParams();
  if (!options.omitCity) params.set("city", String(settings.locality));
  params.set("profile", settings.profile);
  const overrides = Object.values(settings.overrides);
  if (overrides.length > 0) params.set("o", overrides.join(","));
  if (settings.seconds) params.set("sec", "1");
  const candle = settings.candleMinutes[settings.locality];
  if (candle !== undefined) params.set("candle", String(candle));
  if (date) params.set("date", formatPlainDate(date));
  return `?${params.toString()}`;
}
